import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, CheckCircle2 } from "lucide-react";
import SlaProgressBar from "@/components/SlaProgressBar";

export default function AlumnoSoporteThread() {
  const { ticket_number } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/support/tickets/${ticket_number}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [ticket_number]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${data.ticket.id}/messages`, { body: reply.trim() });
      setReply("");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const close = async () => {
    if (!confirm("¿Cerrar este ticket? No podrás reabrirlo.")) return;
    try {
      await api.put(`/support/tickets/${data.ticket.id}/close`, {});
      toast.success("Ticket cerrado.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!data) return <AlumnoLayout user={user}><div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div></AlumnoLayout>;
  const t = data.ticket;
  const isClosed = t.status === "closed";

  return (
    <AlumnoLayout user={user}>
      <Link to="/app/alumno/soporte" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
        <ArrowLeft className="w-3 h-3" /> Volver
      </Link>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-gray-500">{t.ticket_number}</span>
          <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 rounded bg-white/10 text-gray-300">{t.priority}</span>
          <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 rounded bg-white/10 text-gray-300">{t.status}</span>
        </div>
        <h1 className="mt-2 text-2xl font-bold text-white font-display">{t.subject}</h1>
        <p className="mt-1 text-xs text-gray-500">Abierto {new Date(t.created_at).toLocaleString("es-MX")} · SLA respuesta: {t.sla_first_response_hours}h · SLA resolución: {t.sla_resolution_hours}h</p>
      </div>

      <div className="mt-4">
        <SlaProgressBar ticket={t} />
      </div>

      <div className="mt-6 space-y-3" data-testid="soporte-thread-body">
        {data.messages.map((m, i) => {
          const mine = m.sender_type === "user";
          return (
            <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${mine ? "bg-[#F26A21] text-[#0B0F14]" : "bg-white/10 text-white"}`}>
                <p className={`text-[10px] uppercase tracking-widest font-bold ${mine ? "text-[#0B0F14]/70" : "text-gray-300"}`}>
                  {m.sender_name} · {m.sender_type === "staff" ? "Asesor DCC" : "Tú"}
                </p>
                <p className="mt-1 text-sm whitespace-pre-wrap">{m.body}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-[#0B0F14]/60" : "text-gray-500"}`}>
                  {new Date(m.created_at).toLocaleString("es-MX")}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {t.status === "resolved" && !isClosed && (
        <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-emerald-200">Este ticket fue marcado como resuelto. Confirma para cerrarlo.</p>
          <button onClick={close} data-testid="soporte-close-btn"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-500 text-[#0B0F14] px-3 py-1.5 text-xs font-bold hover:brightness-110">
            <CheckCircle2 className="w-3 h-3" /> Confirmar cierre
          </button>
        </div>
      )}

      {!isClosed && (
        <form onSubmit={send} className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-3" data-testid="soporte-reply-form">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)}
                    disabled={sending || isClosed}
                    data-testid="soporte-reply-input"
                    rows={3}
                    className="w-full rounded-md bg-transparent border-0 text-sm text-white placeholder:text-gray-500 outline-none"
                    placeholder="Escribe tu respuesta…" />
          <div className="flex justify-end mt-1">
            <button type="submit" disabled={sending || !reply.trim() || isClosed}
                    data-testid="soporte-reply-btn"
                    className="inline-flex items-center gap-1 rounded-md bg-[#F26A21] text-[#0B0F14] px-3 py-1.5 text-xs font-bold disabled:opacity-50">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Enviar
            </button>
          </div>
        </form>
      )}
    </AlumnoLayout>
  );
}
